/*!
 * VEYR thinking-orb adapter
 * "working" / orbit renderer adapted from Thinking Orbs by Jakub Antalik.
 * Source pinned at cdbcf43cdcc1703efd7a4c14e217e7ec5c551b7a (MIT).
 * Full notice: assets/veyr-jakub-antalik-mit-notice.txt
 */
(function () {
  'use strict';

  function startThinkingOrb() {
    var transition = document.querySelector('[data-veyr-page-transition]');
    var canvas = transition && transition.querySelector('[data-veyr-thinking-orb]');
    if (!transition || !canvas || canvas.dataset.veyrThinkingOrbBound === 'true') return;

    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.dataset.veyrThinkingOrbBound = 'true';

    var TAU = Math.PI * 2;
    var TILT = 0.3;
    var SIN_TILT = Math.sin(TILT);
    var COS_TILT = Math.cos(TILT);
    var ORBIT_COUNT = 12;
    var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    var reducedMotion = Boolean(motionQuery && motionQuery.matches);
    var isMobile = Boolean(mobileQuery && mobileQuery.matches);
    var cssSize = 0;
    var pixelSize = 0;
    var effectiveDpr = 1;
    var ghostCount = isMobile ? 28 : 40;
    var particleCount = isMobile ? 2 : 3;
    var frameInterval = 1000 / (isMobile ? 30 : 45);
    var frameRequest = 0;
    var resizeRequest = 0;
    var visibilityTimer = 0;
    var recoveryTimer = 0;
    var running = false;
    var contextLost = false;
    var lastPaint = 0;
    var renderFaults = 0;
    var staticDirty = true;
    var forceResize = false;
    var orbits = [];
    var dotPool = [];
    var grayColors = [];

    for (var gray = 0; gray < 256; gray += 1) {
      grayColors[gray] = 'rgb(' + gray + ',' + gray + ',' + gray + ')';
    }

    function hashD(a, b) {
      var hash = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
      return hash - Math.floor(hash);
    }

    function isFiniteNumber(value) {
      return typeof value === 'number' && isFinite(value);
    }

    function rebuildGeometry() {
      if (!cssSize) return;

      isMobile = Boolean(mobileQuery && mobileQuery.matches);
      ghostCount = isMobile ? 28 : 40;
      particleCount = isMobile ? 2 : 3;
      frameInterval = 1000 / (isMobile ? 30 : 45);

      var radius = (cssSize / 2) * 0.82;
      var totalDots = ORBIT_COUNT * (ghostCount + particleCount);
      orbits = [];

      while (dotPool.length < totalDots) {
        dotPool.push({ x: 0, y: 0, z: 0, radius: 0, white: 0, alpha: 0 });
      }
      if (dotPool.length > totalDots) dotPool.length = totalDots;

      for (var orbitIndex = 0; orbitIndex < ORBIT_COUNT; orbitIndex += 1) {
        var h1 = hashD(orbitIndex, 1.7);
        var h2 = hashD(orbitIndex, 5.2);
        var h3 = hashD(orbitIndex, 8.9);
        var orbitRadius = radius * (0.45 + 0.52 * h1);
        var theta = h1 * TAU;
        var phi = Math.acos(2 * h2 - 1);
        var nx = Math.sin(phi) * Math.cos(theta);
        var ny = Math.cos(phi);
        var nz = Math.sin(phi) * Math.sin(theta);
        var ux = -ny;
        var uy = nx;
        var uz = 0;
        var unitLength = Math.max(0.000001, Math.sqrt(ux * ux + uy * uy));
        ux /= unitLength;
        uy /= unitLength;
        var vx = ny * uz - nz * uy;
        var vy = nz * ux - nx * uz;
        var vz = nx * uy - ny * ux;
        var ghostPoints = new Float32Array(ghostCount * 3);

        for (var ghostIndex = 0; ghostIndex < ghostCount; ghostIndex += 1) {
          var ghostAngle = (ghostIndex / ghostCount) * TAU;
          var ghostCos = Math.cos(ghostAngle);
          var ghostSin = Math.sin(ghostAngle);
          var offset = ghostIndex * 3;
          ghostPoints[offset] = (ux * ghostCos + vx * ghostSin) * orbitRadius;
          ghostPoints[offset + 1] = (uy * ghostCos + vy * ghostSin) * orbitRadius;
          ghostPoints[offset + 2] = (uz * ghostCos + vz * ghostSin) * orbitRadius;
        }

        orbits.push({
          radius: orbitRadius,
          ux: ux,
          uy: uy,
          uz: uz,
          vx: vx,
          vy: vy,
          vz: vz,
          speed: (0.25 + 0.55 * h3) * (h3 > 0.5 ? 1 : -1),
          phase: h2 * 6,
          ghosts: ghostPoints
        });
      }
    }

    function projectInto(dot, x, y, z, cosYaw, sinYaw, center) {
      var x1 = x * cosYaw + z * sinYaw;
      var z1 = -x * sinYaw + z * cosYaw;
      var y1 = y * COS_TILT - z1 * SIN_TILT;
      dot.x = center + x1;
      dot.y = center - y1;
      dot.z = y * SIN_TILT + z1 * COS_TILT;
    }

    function draw(time) {
      if (!ctx || contextLost || !cssSize || !pixelSize || !isFiniteNumber(time)) return;

      var center = cssSize / 2;
      var radiusScale = Math.pow(cssSize / 300, 0.6);
      var yaw = time * 0.12;
      var sinYaw = Math.sin(yaw);
      var cosYaw = Math.cos(yaw);
      var dotIndex = 0;

      for (var orbitIndex = 0; orbitIndex < orbits.length; orbitIndex += 1) {
        var orbit = orbits[orbitIndex];

        for (var ghostIndex = 0; ghostIndex < ghostCount; ghostIndex += 1) {
          var ghostOffset = ghostIndex * 3;
          var ghostDot = dotPool[dotIndex];
          projectInto(
            ghostDot,
            orbit.ghosts[ghostOffset],
            orbit.ghosts[ghostOffset + 1],
            orbit.ghosts[ghostOffset + 2],
            cosYaw,
            sinYaw,
            center
          );
          var ghostDepth = (ghostDot.z / orbit.radius + 1) / 2;
          ghostDot.radius = 0.9 * radiusScale;
          ghostDot.white = 0.72;
          ghostDot.alpha = 0.5 * (0.4 + 0.6 * ghostDepth);
          dotIndex += 1;
        }

        for (var particleIndex = 0; particleIndex < particleCount; particleIndex += 1) {
          var particleAngle = time * orbit.speed + (particleIndex / particleCount) * TAU + orbit.phase;
          var particleCos = Math.cos(particleAngle);
          var particleSin = Math.sin(particleAngle);
          var particleDot = dotPool[dotIndex];
          projectInto(
            particleDot,
            (orbit.ux * particleCos + orbit.vx * particleSin) * orbit.radius,
            (orbit.uy * particleCos + orbit.vy * particleSin) * orbit.radius,
            (orbit.uz * particleCos + orbit.vz * particleSin) * orbit.radius,
            cosYaw,
            sinYaw,
            center
          );
          var particleDepth = (particleDot.z / orbit.radius + 1) / 2;
          particleDot.radius = (1.2 + 1.6 * particleDepth) * radiusScale;
          particleDot.white = 0.3 - 0.22 * particleDepth;
          particleDot.alpha = 1;
          dotIndex += 1;
        }
      }

      dotPool.sort(function (a, b) { return a.z - b.z; });

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, pixelSize, pixelSize);
      ctx.setTransform(effectiveDpr, 0, 0, effectiveDpr, 0, 0);

      for (var index = 0; index < dotPool.length; index += 1) {
        var dot = dotPool[index];
        if (
          dot.alpha < 0.02 ||
          !isFiniteNumber(dot.x) ||
          !isFiniteNumber(dot.y) ||
          !isFiniteNumber(dot.z) ||
          !isFiniteNumber(dot.radius) ||
          !isFiniteNumber(dot.alpha)
        ) {
          continue;
        }

        var ink = Math.round((1 - Math.max(0, Math.min(1, dot.white))) * 255);
        ctx.globalAlpha = Math.max(0, Math.min(1, dot.alpha));
        ctx.fillStyle = grayColors[ink];
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, Math.max(0.3, dot.radius), 0, TAU);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    }

    function drawFallback() {
      if (!ctx || contextLost || !cssSize || !pixelSize) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, pixelSize, pixelSize);
      ctx.setTransform(effectiveDpr, 0, 0, effectiveDpr, 0, 0);
      ctx.strokeStyle = 'rgba(215,215,215,.72)';
      ctx.lineWidth = Math.max(1, cssSize / 150);
      ctx.beginPath();
      ctx.arc(cssSize / 2, cssSize / 2, cssSize * 0.29, 0, TAU * 0.82);
      ctx.stroke();
    }

    function safeDraw(time) {
      try {
        draw(time);
        renderFaults = 0;
        staticDirty = false;
        return true;
      } catch (error) {
        renderFaults += 1;
        try {
          drawFallback();
        } catch (fallbackError) {
          // A restored context or the next page lifecycle event will retry safely.
        }
        return false;
      }
    }

    function nowMilliseconds() {
      return window.performance && typeof window.performance.now === 'function'
        ? window.performance.now()
        : Date.now();
    }

    function orbTime(milliseconds) {
      return (milliseconds / 1000) * 1.885;
    }

    function transitionIsVisible() {
      if (document.body.classList.contains('veyr-page-leaving')) return true;
      var style = window.getComputedStyle(transition);
      var opacity = parseFloat(style.opacity);
      return style.display !== 'none' && (isNaN(opacity) || opacity > 0.01);
    }

    function shouldRun() {
      return !reducedMotion && !contextLost && !document.hidden && transitionIsVisible();
    }

    function clearRecovery() {
      if (recoveryTimer) {
        window.clearTimeout(recoveryTimer);
        recoveryTimer = 0;
      }
    }

    function stop() {
      running = false;
      lastPaint = 0;
      clearRecovery();
      if (frameRequest) {
        window.cancelAnimationFrame(frameRequest);
        frameRequest = 0;
      }
    }

    function scheduleRecovery() {
      clearRecovery();
      if (renderFaults >= 3 || contextLost) return;
      recoveryTimer = window.setTimeout(function () {
        recoveryTimer = 0;
        sync();
      }, 180);
    }

    function frame(timestamp) {
      frameRequest = 0;
      if (!running || contextLost) return;

      if (!lastPaint || timestamp - lastPaint >= frameInterval) {
        lastPaint = timestamp;
        if (!safeDraw(orbTime(timestamp))) {
          running = false;
          scheduleRecovery();
          return;
        }
      }

      if (running && !frameRequest) {
        frameRequest = window.requestAnimationFrame(frame);
      }
    }

    function start() {
      if (running || frameRequest || contextLost || !cssSize) return;
      clearRecovery();
      running = true;
      lastPaint = 0;

      if (!safeDraw(orbTime(nowMilliseconds()))) {
        running = false;
        scheduleRecovery();
        return;
      }

      frameRequest = window.requestAnimationFrame(frame);
    }

    function sync() {
      if (!cssSize) {
        queueResize(true);
        return;
      }

      if (reducedMotion) {
        stop();
        if (staticDirty && transitionIsVisible()) safeDraw(0.6);
        return;
      }

      if (shouldRun()) start();
      else stop();
    }

    function resizeCanvas(force) {
      if (contextLost) return;

      var rect = canvas.getBoundingClientRect();
      var nextSize = Math.round(Math.min(rect.width, rect.height));
      if (!nextSize) return;

      var nextMobile = Boolean(mobileQuery && mobileQuery.matches);
      var targetDpr = Math.min(nextMobile ? 1.5 : 2, window.devicePixelRatio || 1);
      var nextPixelSize = Math.max(1, Math.round(nextSize * targetDpr));
      var sizeChanged = nextSize !== cssSize;
      var pixelsChanged = nextPixelSize !== pixelSize;
      var modeChanged = nextMobile !== isMobile;

      if (!sizeChanged && !pixelsChanged && !modeChanged && !force) return;

      cssSize = nextSize;
      pixelSize = nextPixelSize;
      effectiveDpr = pixelSize / cssSize;

      if (pixelsChanged || canvas.width !== pixelSize || canvas.height !== pixelSize) {
        canvas.width = pixelSize;
        canvas.height = pixelSize;
      }

      if (sizeChanged || modeChanged || !orbits.length) rebuildGeometry();
      staticDirty = true;

      if (!running && transitionIsVisible()) {
        safeDraw(reducedMotion ? 0.6 : orbTime(nowMilliseconds()));
      }
    }

    function queueResize(force) {
      forceResize = forceResize || Boolean(force);
      if (resizeRequest) return;

      resizeRequest = window.requestAnimationFrame(function () {
        var requestedForce = forceResize;
        resizeRequest = 0;
        forceResize = false;
        resizeCanvas(requestedForce);
        sync();
      });
    }

    function handleBodyClassChange() {
      sync();
      if (visibilityTimer) window.clearTimeout(visibilityTimer);
      visibilityTimer = window.setTimeout(function () {
        visibilityTimer = 0;
        sync();
      }, 720);
    }

    function handleVisibilityChange() {
      if (!document.hidden) queueResize(true);
      sync();
    }

    function handlePageHide() {
      stop();
      if (resizeRequest) {
        window.cancelAnimationFrame(resizeRequest);
        resizeRequest = 0;
      }
      if (visibilityTimer) {
        window.clearTimeout(visibilityTimer);
        visibilityTimer = 0;
      }
    }

    function handlePageShow() {
      staticDirty = true;
      queueResize(true);
    }

    function handleContextLost(event) {
      if (event && event.preventDefault) event.preventDefault();
      contextLost = true;
      stop();
    }

    function handleContextRestored() {
      contextLost = false;
      ctx = canvas.getContext('2d');
      cssSize = 0;
      pixelSize = 0;
      orbits = [];
      dotPool = [];
      staticDirty = true;
      queueResize(true);
    }

    var bodyObserver = new MutationObserver(handleBodyClassChange);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    transition.addEventListener('transitionend', sync);
    transition.addEventListener('transitioncancel', sync);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('resize', function () { queueResize(false); }, { passive: true });
    window.addEventListener('orientationchange', function () { queueResize(true); }, { passive: true });
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    canvas.addEventListener('contextlost', handleContextLost);
    canvas.addEventListener('contextrestored', handleContextRestored);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () { queueResize(false); }, { passive: true });
    }

    if (window.ResizeObserver) {
      var canvasObserver = new ResizeObserver(function () { queueResize(false); });
      canvasObserver.observe(canvas);
    }

    if (motionQuery) {
      var updateMotion = function (event) {
        reducedMotion = event.matches;
        staticDirty = true;
        queueResize(true);
      };
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', updateMotion);
      else if (motionQuery.addListener) motionQuery.addListener(updateMotion);
    }

    if (mobileQuery) {
      var updateViewportMode = function () {
        staticDirty = true;
        queueResize(true);
      };
      if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', updateViewportMode);
      else if (mobileQuery.addListener) mobileQuery.addListener(updateViewportMode);
    }

    queueResize(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startThinkingOrb, { once: true });
  } else {
    startThinkingOrb();
  }
})();
